import React from 'react';
import { ArrowRight } from 'lucide-react';

import { Dialog, DialogContent, DialogTitle } from '../ui/dialog';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { cn } from '../../lib/utils';
import { getUseCases } from '../../domains/genogram/config/useCases';
import type { UseCaseType } from '../../domains/genogram/models/UseCase';
import { useTranslation } from '@/hooks/useTranslation';
import { Tracker } from '../../domains/analytics/services/Tracker';
import { GENOGRAM_WIZARD } from '../../domains/analytics/constants/MetricEvents';
import {
  GENOGRAM_WIZARD_GRID_TEST_ID,
  GENOGRAM_WIZARD_START_TEMPLATE_TEST_ID,
  getGenogramWizardCardTestId,
} from '../../domains/genogram/start/genogramStartSurface';

interface GenoWizardDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirmUseCase: (useCaseId: UseCaseType) => Promise<void>;
}

export const GenoWizardDialog: React.FC<GenoWizardDialogProps> = ({
  open,
  onOpenChange,
  onConfirmUseCase,
}) => {
  const { t, i18n } = useTranslation('genogram');
  const useCases = React.useMemo(() => getUseCases(i18n.language), [i18n.language]);
  const [hoveredUseCase, setHoveredUseCase] = React.useState<UseCaseType | null>(null);
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const didTrackViewedRef = React.useRef(false);

  React.useEffect(() => {
    if (!open || didTrackViewedRef.current) {
      return;
    }

    didTrackViewedRef.current = true;
    Tracker.track(GENOGRAM_WIZARD.VIEWED, {
      optionCount: useCases.length,
    });
  }, [open, useCases.length]);

  const handleSelectUseCase = async (useCaseId: UseCaseType) => {
    if (isSubmitting) {
      return;
    }

    setIsSubmitting(true);
    try {
      await onConfirmUseCase(useCaseId);
      onOpenChange(false);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className={cn(
          'z-[1001] gap-0 border-0 bg-[#F8FAFC] p-0 shadow-2xl',
          'w-[min(984px,calc(100vw-32px))] max-h-[calc(100vh-32px)] overflow-y-auto rounded-xl',
          'sm:max-w-[984px] 2xl:w-[min(860px,calc(100vw-32px))] 2xl:max-w-[860px]',
        )}
        overlayClassName="z-[1000] bg-slate-900/65"
        showCloseButton={false}
        onPointerDownOutside={(event) => event.preventDefault()}
      >
        <div className="px-6 pb-6 pt-6 md:px-8 max-[900px]:pb-5 max-[900px]:pt-5 xl:px-12 xl:pb-6 xl:pt-6 2xl:pb-9 2xl:pt-9">
          <div className="text-center">
            <DialogTitle className="font-['Inter'] text-2xl font-semibold leading-none tracking-normal text-[#20293A]">
              {t('useCaseSelection.title')}
            </DialogTitle>
            <p className="mt-2 font-['Inter'] text-xs font-normal leading-none text-[#364153] 2xl:mt-3">
              {t('useCaseSelection.subtitle')}
            </p>
          </div>

          <div
            className="mt-5 grid grid-cols-1 justify-items-center gap-3 md:grid-cols-2 xl:grid-cols-3 2xl:mt-9"
            data-testid={GENOGRAM_WIZARD_GRID_TEST_ID}
          >
            {useCases.map((useCase) => {
              const isHovered = hoveredUseCase === useCase.id;

              return (
                <button
                  key={useCase.id}
                  type="button"
                  onClick={() => void handleSelectUseCase(useCase.id)}
                  onMouseEnter={() => setHoveredUseCase(useCase.id)}
                  onMouseLeave={() => setHoveredUseCase(null)}
                  disabled={isSubmitting}
                  className={cn(
                    'relative flex min-h-[114px] w-full cursor-pointer flex-col rounded-lg border bg-white px-4 py-[9px] text-left transition xl:min-h-[184px] 2xl:max-w-[260px] xl:pb-6 xl:pt-6 2xl:min-h-[196px]',
                    'hover:shadow-md',
                    'focus-visible:outline-none',
                    isHovered ? 'border-transparent' : 'border-slate-200',
                  )}
                  data-testid={getGenogramWizardCardTestId(useCase.id)}
                >
                  {isHovered ? (
                    <span
                      className="pointer-events-none absolute inset-[-2px] rounded-[10px]"
                      style={{
                        border: '2px solid transparent',
                        background:
                          'linear-gradient(#ffffff, #ffffff) padding-box, linear-gradient(116.22deg, #6466E9 16.5%, #3D9DF2 83.5%) border-box',
                      }}
                      aria-hidden
                    />
                  ) : null}
                  {isHovered ? (
                    <ArrowRight className="absolute right-5 top-5 z-10 size-5 text-[#4F83EE] 2xl:right-6 2xl:size-6" aria-hidden />
                  ) : null}

                  <span className="relative z-10 flex items-center gap-4 xl:block">
                    <span className="text-black [&_svg]:size-10 xl:mb-3 xl:block xl:[&_svg]:size-9 [&_svg]:stroke-[2]" aria-hidden>
                      {useCase.icon}
                    </span>
                    <span className="font-['Inter'] text-[15px] font-medium leading-6 tracking-[-0.31px] text-[#0A0A0A]">
                      {useCase.id === 'blank' ? 'Blank Canvas' : useCase.name}
                    </span>
                  </span>
                  <span className="relative z-10 mt-3 hidden font-['Inter'] text-xs font-medium leading-[1.1] tracking-[-0.15px] text-[#4A5567] xl:block">
                    {useCase.description}
                  </span>

                  {useCase.professionals.length > 0 ? (
                    <span className="relative z-10 mt-2 flex flex-wrap gap-2 xl:mt-3">
                      {useCase.professionals.map((professional) => (
                        <Badge
                          key={professional}
                          variant="secondary"
                          className="rounded-[4px] bg-[#F2F5F9] p-1 font-['Inter'] text-[10px] font-medium leading-none tracking-[-0.15px] text-[#20293A] shadow-none"
                        >
                          {professional}
                        </Badge>
                      ))}
                    </span>
                  ) : null}
                </button>
              );
            })}
          </div>

          <div className="mt-6 flex justify-center 2xl:mt-9">
            <Button
              type="button"
              onClick={() => {
                Tracker.track(GENOGRAM_WIZARD.TEMPLATE_STARTED);
                onOpenChange(false);
              }}
              className={cn(
                'h-12 rounded-[8px] bg-gradient-to-r from-[#6265F0] to-[#2FA3F7] px-5 font-["Inter"] text-base font-semibold text-white 2xl:h-12',
                'hover:from-[#5659E5] hover:to-[#2497EB]',
              )}
              data-testid={GENOGRAM_WIZARD_START_TEMPLATE_TEST_ID}
            >
              Start with a template
              <ArrowRight className="size-6" aria-hidden />
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
